import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import BrandSettingsLayout from "@/components/brand/settings/brand-settings-layout"
import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import { brandSettingsInvitesPath } from "@/routes"
import {
  type BrandSettingsInvitesProps,
  INVITE_EXPIRATION_MAX_DAYS,
  INVITE_EXPIRATION_MIN_DAYS,
} from "@/types/invites"

/**
 * OKS-21 — Brand Settings → Invites page.
 *
 * Single numeric form field — "Default invite expiration (days)",
 * range 1..90 (locked with BE TRD §Range cap). Submits to
 * `brandSettingsInvitesPath()` via Inertia `<Form method="patch">`
 * with form binding `name="brand[invite_link_expiration_days]"`.
 *
 * **Wire-format canary** (Vitest spec) asserts the literal `name=`,
 * `method=`, and js-routes `action` URL — catches Rails strong-params
 * drift, route helper rename, and HTTP-verb drift in one shot per
 * memory `vitest_wire_format_canary`.
 *
 * **No shadcn `<Form>`** — Inertia `<Form>` is the contract per Crew
 * rules. Errors flow through the render-prop `errors` object;
 * `<InputError>` renders inline.
 *
 * **BugFix D18 — read-only mode for `tenant_role: "member"` users.**
 * Backend emits `can_edit: boolean` per `BrandSettingsInvitesPolicy
 * #update?`. When false, the input renders `readonly`, the Save
 * button is hidden, and a one-liner explains the read-only state.
 * Server gates the affordance; FE renders. Mirrors OKS-19's
 * `actions.{verb}_url: string|null` pattern.
 *
 * **Flash → toast.** Backend's `#update` controller emits
 * `flash[:success]` on save → `useFlash` (mounted in PersistentLayout)
 * fires the success toast.
 */

export default function BrandSettingsInvites({
  brand,
  can_edit,
}: BrandSettingsInvitesProps) {
  const { t } = useTranslation()

  return (
    <AppLayout>
      <Head title={t("invites.settings.title")} />
      <BrandSettingsLayout>
        <div data-testid="brand-invites-settings" className="space-y-6">
          <HeadingSmall
            title={t("invites.settings.title")}
            description={t("invites.settings.subtitle")}
          />

          <Form
            method="patch"
            action={brandSettingsInvitesPath()}
            options={{ preserveScroll: true }}
            className="space-y-6"
            data-testid="brand-invites-settings-form"
          >
            {({ errors, processing }) => (
              <>
                <div className="grid gap-2">
                  <Label htmlFor="invite_link_expiration_days">
                    {t("invites.settings.expiration.label")}
                  </Label>
                  <Input
                    id="invite_link_expiration_days"
                    name="brand[invite_link_expiration_days]"
                    type="number"
                    step={1}
                    inputMode="numeric"
                    defaultValue={brand.invite_link_expiration_days}
                    readOnly={!can_edit}
                    aria-readonly={!can_edit}
                    aria-describedby="invite_link_expiration_days_helper"
                    aria-invalid={
                      errors.invite_link_expiration_days ? true : undefined
                    }
                    data-testid="brand-invites-settings-expiration-input"
                    className="max-w-[180px]"
                  />
                  <p
                    id="invite_link_expiration_days_helper"
                    className="text-muted-foreground text-sm"
                  >
                    {t("invites.settings.expiration.helper", {
                      min: INVITE_EXPIRATION_MIN_DAYS,
                      max: INVITE_EXPIRATION_MAX_DAYS,
                    })}
                  </p>
                  {!can_edit && (
                    <p
                      data-testid="brand-invites-settings-readonly-hint"
                      role="status"
                      className="text-muted-foreground text-sm"
                    >
                      {t("invites.settings.read_only_hint")}
                    </p>
                  )}
                  <InputError
                    messages={errors.invite_link_expiration_days}
                    data-testid="brand-invites-settings-error-expiration"
                  />
                </div>

                {can_edit && (
                  <Button
                    type="submit"
                    disabled={processing}
                    data-testid="brand-invites-settings-save"
                  >
                    {processing
                      ? t("invites.settings.saving")
                      : t("invites.settings.save")}
                  </Button>
                )}
              </>
            )}
          </Form>
        </div>
      </BrandSettingsLayout>
    </AppLayout>
  )
}
